import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { useTranslation } from 'react-i18next'
import { usePage } from '@inertiajs/react'
import { Button } from '@/components/ui/button'
import { RelativeTime } from '@/components/ui/relative-time'
import { CheckCircle2, Hammer, PenTool, Play, X, MousePointerClick, ShieldAlert } from 'lucide-react'
import { isAdmin } from '@/lib/auth'
import type { Message, ConfirmationButtonData, ConfirmationButtonAction } from '@/types/conversations'

const ACTION_ICONS: Record<string, typeof MousePointerClick> = {
  'Build it': Hammer,
  'Draw prototype': PenTool,
  'Start': Play,
  'Confirm': Play,
  'Cancel': X,
}

function getActionIcon(label: string) {
  const Icon = ACTION_ICONS[label] || MousePointerClick
  return <Icon className="h-4 w-4" />
}

interface ConfirmationButtonMessageProps {
  message: Message
  onConfirm: (messageId: number, prompt: string | undefined, clickedLabel?: string) => Promise<void>
  isProcessing?: boolean
}

function getContentWithoutButton(content: string): string {
  return content
    .replace(/<confirmation_button>[\s\S]*?<\/confirmation_button>/g, '')
    .replace(/<clarifying_questions>[\s\S]*?<\/clarifying_questions>/g, '')
    .replace(/<report>[\s\S]*?<\/report>/gi, '')
    .replace(/<level_upgrade_suggestion>.*?<\/level_upgrade_suggestion>/gi, '')
    .replace(/<next_step_suggestions>[\s\S]*?<\/next_step_suggestions>/gi, '')
    .trim()
}

function getActions(buttonData: ConfirmationButtonData): ConfirmationButtonAction[] {
  if (buttonData.actions?.length) return buttonData.actions
  if (buttonData.label) return [{ label: buttonData.label }]
  return []
}

export function ConfirmationButtonMessage({ message, onConfirm, isProcessing = false }: ConfirmationButtonMessageProps) {
  const { t } = useTranslation('conversations')
  const { props } = usePage<any>()
  const buttonData: ConfirmationButtonData | null = message.confirmation_button_data || null
  const contentWithoutButton = getContentWithoutButton(message.content)
  const [submittingLabel, setSubmittingLabel] = useState<string | null>(null)

  const isClicked = message.confirmation_clicked
  const isSkipped = message.form_skipped && !message.confirmation_clicked
  const requiresAdmin = buttonData?.requires_admin === true
  const userIsAdmin = isAdmin(props.auth?.user)

  const handleConfirm = async (label: string) => {
    if (!buttonData) return

    setSubmittingLabel(label)
    try {
      await onConfirm(message.id, buttonData.prompt, label)
    } catch (error) {
      console.error('Failed to confirm:', error)
    } finally {
      setSubmittingLabel(null)
    }
  }

  if (!buttonData) {
    return (
      <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 min-w-0 mt-4">
        <div className="flex flex-col gap-1 min-w-0 flex-1 max-w-full">
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm [overflow-wrap:anywhere]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{getContentWithoutButton(message.content)}</ReactMarkdown>
          </div>
          <RelativeTime timestamp={message.created_at} className="text-xs text-muted-foreground" />
        </div>
      </div>
    )
  }

  const actions = getActions(buttonData)
  const clickedLabel = buttonData.clicked_action || buttonData.label || actions[0]?.label

  return (
    <div className="flex gap-3 animate-in fade-in slide-in-from-left-2 duration-300 min-w-0 mt-4">
      <div className="flex flex-col gap-2 min-w-0 flex-1 max-w-full">
        {contentWithoutButton && (
          <div className="prose prose-sm dark:prose-invert max-w-none text-sm mb-2 [overflow-wrap:anywhere]">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>{contentWithoutButton}</ReactMarkdown>
          </div>
        )}

        {isClicked ? (
          <div className="inline-flex items-center gap-2 cursor-default px-3 py-1.5 rounded-md border border-green-500/30 bg-green-50/30 dark:bg-green-950/10 w-fit">
            <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400 flex-shrink-0" />
            <span className="text-sm text-green-700 dark:text-green-300">
              {clickedLabel === 'Confirm' ? t('chat.confirmed') : `${clickedLabel} - ${t('chat.confirmed')}`}
            </span>
          </div>
        ) : requiresAdmin && !userIsAdmin ? (
          <div className="flex items-center gap-2 cursor-default">
            <ShieldAlert className="h-4 w-4 text-amber-500 flex-shrink-0" />
            <span className="text-sm text-muted-foreground">
              {t('chat.adminRequired', 'Only organization admins can approve this operation.')}
            </span>
          </div>
        ) : isSkipped ? (
          <div className="inline-flex items-center gap-2 cursor-default px-3 py-1.5 rounded-md border border-border bg-muted/30 w-fit">
            <X className="h-4 w-4 text-muted-foreground/50 flex-shrink-0" />
            <span className="text-sm text-muted-foreground/50 line-through">
              {actions.map(a => a.label).join(' / ')}
            </span>
          </div>
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            {actions.map((action, i) => (
              <Button
                key={action.label}
                onClick={() => handleConfirm(action.label)}
                disabled={submittingLabel !== null || isProcessing}
                variant={i === 0 ? 'default' : 'outline'}
                className="gap-2"
                size="sm"
              >
                {getActionIcon(action.label)}
                {submittingLabel === action.label
                  ? t('chat.processing')
                  : action.label}
              </Button>
            ))}
          </div>
        )}

        <RelativeTime timestamp={message.created_at} className="text-xs text-muted-foreground" />
      </div>
    </div>
  )
}
